import { Input, Dropdown, IconButton, MenuAction, MenuDivider } from '@kiipu/ui';
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  ArrowClockwise,
  ArrowSquareOut,
  DotsThree,
  FolderSimple,
  ImageBroken,
  LinkSimple,
  Star,
  Trash,
  VideoCamera,
  Check,
  PencilSimple,
} from '@phosphor-icons/react';

import { formatDate, platformName, safeHttpUrl, statusLabel, type LibraryItem } from '../api';
import { canMutate, desktopBridge, privateMediaUrl } from '../lib/client-platform';
import { SearchMatchPreview } from './search-match';
import { SiteIcon } from './site-icon';

export type ViewMode = 'list' | 'card' | 'masonry';
export type ItemActions = {
  open: (id: string) => void;
  favorite: (item: LibraryItem) => void;
  trash: (item: LibraryItem) => void;
  organize: (item: LibraryItem) => void;
  refresh: (item: LibraryItem) => void;
  remove: (item: LibraryItem) => void;
  copy: (item: LibraryItem) => void;
};
export function ItemMenu({
  item,
  actions,
  onRename,
  children,
}: {
  item: LibraryItem;
  actions: ItemActions;
  onRename?: () => void;
  children?: ReactNode;
}) {
  return (
    <Dropdown label="更多操作" icon={<DotsThree size={19} />}>
      <MenuAction disabled={!canMutate()} onClick={() => actions.favorite(item)}>
        <Star size={15} weight={item.favorite ? 'fill' : 'regular'} />
        {item.favorite ? '取消收藏' : '加入收藏'}
      </MenuAction>
      <MenuAction disabled={!canMutate()} onClick={() => actions.organize(item)}>
        <FolderSimple size={15} />
        整理内容
      </MenuAction>
      {onRename && !item.deletedAt ? (
        <MenuAction disabled={!canMutate()} onClick={onRename}>
          <PencilSimple size={15} />
          修改标题
        </MenuAction>
      ) : null}
      <MenuAction
        onClick={() => {
          const url = safeHttpUrl(item.url);
          if (url) {
            const bridge = desktopBridge();
            if (bridge) void bridge.openExternal(url);
            else window.open(url, '_blank', 'noopener,noreferrer');
          }
        }}
      >
        <ArrowSquareOut size={15} />
        打开原文
      </MenuAction>
      <MenuAction onClick={() => actions.copy(item)}>
        <LinkSimple size={15} />
        复制链接
      </MenuAction>
      {!item.deletedAt ? (
        <MenuAction
          onClick={() => actions.refresh(item)}
          disabled={!canMutate() || item.status === 'queued' || item.status === 'running'}
        >
          <ArrowClockwise size={15} />
          重新获取
        </MenuAction>
      ) : null}
      {children}
      <MenuDivider />
      <MenuAction
        disabled={!canMutate()}
        danger={!item.deletedAt}
        onClick={() => actions.trash(item)}
      >
        {item.deletedAt ? <ArrowClockwise size={15} /> : <Trash size={15} />}
        {item.deletedAt ? '恢复内容' : '移入回收站'}
      </MenuAction>
      {item.deletedAt ? (
        <MenuAction disabled={!canMutate()} danger onClick={() => actions.remove(item)}>
          <Trash size={15} />
          永久删除
        </MenuAction>
      ) : null}
    </Dropdown>
  );
}
export function CaptureState({ item }: { item: LibraryItem }) {
  if (item.status === 'completed' && item.quality === 'complete') return null;
  return (
    <span
      className={`capture-state ${item.status === 'running' || item.status === 'queued' ? 'capture-pending' : 'capture-attention'}`}
    >
      {item.status === 'running' || item.status === 'queued' ? (
        <span className="status-dot" />
      ) : null}
      {statusLabel(item)}
    </span>
  );
}
function Cover({ item, masonry }: { item: LibraryItem; masonry: boolean }) {
  const [failed, setFailed] = useState(false);
  const url = privateMediaUrl(item.coverUrl);
  if (!url || failed) return null;
  return (
    <div className={`card-cover${masonry ? ' masonry-cover' : ''}`}>
      <img src={url} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
      {item.kind === 'video' ? (
        <span className="video-badge">
          <VideoCamera size={14} />
          视频
        </span>
      ) : null}
    </div>
  );
}
export function ItemCard({
  item,
  selected,
  checked,
  selecting,
  actions,
  onCheck,
  masonry,
}: {
  item: LibraryItem;
  selected: boolean;
  checked: boolean;
  selecting: boolean;
  actions: ItemActions;
  onCheck: (id: string) => void;
  masonry: boolean;
}) {
  return (
    <article
      className={`item-card ${selected ? 'selected' : ''}`}
      data-selected={checked}
      data-has-cover={Boolean(item.coverUrl)}
      data-item-id={item.id}
    >
      <button
        type="button"
        className="card-open"
        onClick={() => (selecting ? onCheck(item.id) : actions.open(item.id))}
        aria-label={`${selecting ? '选择' : '阅读'} ${item.title || item.url}`}
        aria-pressed={selecting ? checked : undefined}
      >
        <Cover key={item.coverUrl} item={item} masonry={masonry} />
        <div className="card-body">
          <div className="card-source">
            <SiteIcon item={item} />
            <span>{item.siteName || platformName(item.platform)}</span>
            {selecting ? (
              <span className={`selection-box ${checked ? 'checked' : ''}`}>
                {checked ? <Check size={12} /> : null}
              </span>
            ) : null}
          </div>
          <h2>{item.title || item.url}</h2>
          {item.searchMatch ? (
            <p className="card-text card-search-match">
              <SearchMatchPreview match={item.searchMatch} />
            </p>
          ) : !item.coverUrl ? (
            <p className="card-text">
              {item.note ||
                (item.status === 'queued' || item.status === 'running'
                  ? '链接已经保存，正在为你获取内容。'
                  : item.author
                    ? `来自 ${item.author} 的${item.kind === 'post' ? '笔记' : '文章'}`
                    : item.url)}
            </p>
          ) : null}
        </div>
      </button>
      <div className="card-footer">
        <div className="card-meta">
          <time dateTime={item.createdAt}>{formatDate(item.createdAt)}</time>
          <CaptureState item={item} />
        </div>
        <div className="row card-actions">
          <IconButton
            label={item.favorite ? '取消收藏' : '加入收藏'}
            disabled={!canMutate()}
            aria-pressed={item.favorite}
            className={item.favorite ? 'favorite' : ''}
            onClick={() => actions.favorite(item)}
          >
            <Star size={16} weight={item.favorite ? 'fill' : 'regular'} />
          </IconButton>
          <ItemMenu item={item} actions={actions} />
        </div>
      </div>
    </article>
  );
}
export function ItemRow({
  item,
  selected,
  checked,
  selecting,
  actions,
  onCheck,
}: {
  item: LibraryItem;
  selected: boolean;
  checked: boolean;
  selecting: boolean;
  actions: ItemActions;
  onCheck: (id: string) => void;
}) {
  return (
    <article
      className={`item-row ${selected ? 'selected' : ''}`}
      data-selected={checked}
      data-item-id={item.id}
      data-search-match={Boolean(item.searchMatch)}
    >
      {selecting ? (
        <label className="row-check">
          <Input
            type="checkbox"
            checked={checked}
            onChange={() => onCheck(item.id)}
            aria-label={`选择 ${item.title || item.url}`}
          />
        </label>
      ) : null}
      <button
        className="row-open"
        onClick={() => (selecting ? onCheck(item.id) : actions.open(item.id))}
      >
        <SiteIcon item={item} />
        <span className="row-copy">
          <span className="row-source">
            {item.siteName || platformName(item.platform)}
            <span className="row-author">{item.author}</span>
          </span>
          <span className="row-title">{item.title || item.url}</span>
          <span className="row-preview">
            <CaptureState item={item} />
            {item.searchMatch ? (
              <SearchMatchPreview match={item.searchMatch} />
            ) : (
              item.note || (item.kind === 'video' ? '视频 · 说明、封面与原始链接' : item.url)
            )}
          </span>
        </span>
      </button>
      <div className="row-trailing">
        <time dateTime={item.createdAt}>{formatDate(item.createdAt)}</time>
        <IconButton
          className={item.favorite ? 'favorite' : ''}
          disabled={!canMutate()}
          label={item.favorite ? '取消收藏' : '加入收藏'}
          aria-pressed={item.favorite}
          onClick={() => actions.favorite(item)}
        >
          <Star size={15} weight={item.favorite ? 'fill' : 'regular'} />
        </IconButton>
        <ItemMenu item={item} actions={actions} />
      </div>
    </article>
  );
}
export function CollectionLayout({
  mode,
  children,
  count,
  searching = false,
}: {
  mode: ViewMode;
  children: ReactNode;
  count: number;
  searching?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const container = ref.current;
    if (!container || mode === 'list') return;
    let frame = 0;
    const layout = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const width = container.clientWidth;
        const gap = parseFloat(getComputedStyle(container).columnGap) || 12;
        const split = container.closest('.workspace')?.getAttribute('data-split') === 'true';
        const columns = Math.max(
          width >= (split ? 384 + gap : 300) ? 2 : 1,
          Math.floor((width + gap) / (240 + gap)),
        );
        const columnWidth = (width - (columns - 1) * gap) / columns;
        container.style.setProperty('--columns', String(columns));
        container.style.setProperty(
          '--card-height',
          `${(columnWidth - 2) * 0.5625 + (columnWidth < 240 ? 88 : 92) + (searching ? 48 : 0) + (matchMedia('(max-width:767px)').matches ? 48 : 40) + 2}px`,
        );
        if (mode !== 'masonry') return;
        const lanes = Array.from({ length: columns }, () => 0);
        for (const child of Array.from(container.children) as HTMLElement[]) {
          child.style.width = `${columnWidth}px`;
          const lane = lanes.indexOf(Math.min(...lanes));
          child.style.transform = `translate(${lane * (columnWidth + gap)}px,${lanes[lane]}px)`;
          lanes[lane] += child.offsetHeight + gap;
        }
        container.style.height = `${Math.max(0, ...lanes) - (count ? gap : 0)}px`;
      });
    };
    const observer = new ResizeObserver(layout);
    observer.observe(container);
    for (const child of Array.from(container.children)) observer.observe(child);
    layout();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [mode, count, searching]);
  return (
    <div
      key={mode}
      ref={ref}
      className={`collection collection-${mode}`}
      data-mode={mode}
      data-searching={searching}
    >
      {children}
    </div>
  );
}
export function CollectionSkeleton({ mode }: { mode: ViewMode }) {
  return (
    <div
      className={`collection-skeleton ${mode === 'list' ? 'skeleton-list' : 'skeleton-grid'}`}
      role="status"
      aria-label="正在加载内容"
    >
      {Array.from({ length: 8 }, (_, index) =>
        mode === 'list' ? (
          <div className="skeleton-row" key={index}>
            <div className="skeleton" />
            <div>
              <span className="skeleton" />
              <span className="skeleton" />
            </div>
          </div>
        ) : (
          <div className="skeleton-card" key={index}>
            <div className="skeleton skeleton-cover" />
            <div className="skeleton-card-body">
              <span className="skeleton" />
              <span className="skeleton" />
              <span className="skeleton" />
            </div>
          </div>
        ),
      )}
    </div>
  );
}
